import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Sparkles, Download, Loader2, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 4K-class output at 4:5 vertical aspect — pristine for IG / X.
const OUT_W = 2160;
const OUT_H = 2700;

// Real $PAUL contract address — watermarked on every generated meme.
const CONTRACT_ADDRESS = "CWvAZdgRxPkKufEz2qt6DT65b1H9LDqjwuDu75Fspump";

// ----- Paul templates -----
// `src` is the original (used when bg type === "default")
// `cutout` is the pre-rendered transparent PNG (used for "color" + "scene")
// `cutoutFit`: optional placement override for the cutout layer.
//    { scale: number, anchorY: 0..1 }  (anchorY 0 = top, 0.5 = centre, 1 = bottom)
const TEMPLATES = [
  { id: "standing", label: "Standing", src: "/memes/standing.jpg", cutout: "/memes/cutout-standing.png", objectPosition: "50% 30%" },
  { id: "smiling",  label: "Smiling",  src: "/memes/smiling.jpg",  cutout: "/memes/cutout-smiling.png",  objectPosition: "50% 40%" },
  { id: "fuckyou",  label: "Fuck You", src: "/memes/fuckyou.jpg",  cutout: "/memes/cutout-fuckyou.png",  objectPosition: "50% 35%" },
  { id: "thinking", label: "Thinking", src: "/memes/thinking.jpg", cutout: "/memes/cutout-thinking.png", objectPosition: "50% 40%" },
  { id: "chilling", label: "Chilling", src: "/memes/chilling.jpg", cutout: "/memes/cutout-chilling.png", objectPosition: "50% 50%" },
];

// ----- Backgrounds -----
//  type: "default" => keep the cosmic bg from the source image
//  type: "color"   => flat solid colour
//  type: "scene"   => composite Paul over a scenic photo
const BACKGROUNDS = [
  { id: "default",       type: "default", label: "Default" },

  { id: "purple",        type: "color",   label: "Purple", value: "#7A3EFF" },
  { id: "indigo",        type: "color",   label: "Indigo", value: "#4338CA" },
  { id: "blue",          type: "color",   label: "Blue",   value: "#2563EB" },
  { id: "green",         type: "color",   label: "Green",  value: "#2EC87A" },
  { id: "yellow",        type: "color",   label: "Yellow", value: "#F4CC07" },
  { id: "orange",        type: "color",   label: "Orange", value: "#FF8A00" },
  { id: "red",           type: "color",   label: "Red",    value: "#DC2626" },

  { id: "area-51",       type: "scene",   label: "Area 51",       src: "/scenes/area-51.png" },
  { id: "roswell",       type: "scene",   label: "Roswell",       src: "/scenes/roswell.png" },
  { id: "wyoming-crash", type: "scene",   label: "Wyoming '47",   src: "/scenes/wyoming-crash.png" },
  { id: "rural-highway", type: "scene",   label: "Rural Highway", src: "/scenes/rural-highway.png" },
  { id: "desert-motel",  type: "scene",   label: "Desert Motel",  src: "/scenes/desert-motel.png" },
  { id: "national-park", type: "scene",   label: "National Park", src: "/scenes/national-park.png" },
  { id: "billboard",     type: "scene",   label: "Conspiracy Billboard", src: "/scenes/billboard.png" },
  { id: "diner",         type: "scene",   label: "Burger Diner",  src: "/scenes/diner.png" },
  { id: "government",    type: "scene",   label: "Gov Building",  src: "/scenes/government.png" },
  { id: "suburb",        type: "scene",   label: "Suburb",        src: "/scenes/suburb.png" },
  // NOTE: gas-station scene skipped — Universal LLM key budget hit during one-shot generation.
  //       Re-run /app/backend/scripts/generate_scenes.py after topping up to add it back.
];

// ----- helpers -----
const imageCache = new Map();
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (imageCache.has(src)) return resolve(imageCache.get(src));
    const img = new Image();
    // NOTE: no crossOrigin — every image we use is same-origin (frontend public/).
    // Setting crossOrigin can cause silent load failures if CORS headers are absent.
    img.onload = () => { imageCache.set(src, img); resolve(img); };
    img.onerror = () => reject(new Error(`Image failed to load: ${src}`));
    img.src = src;
  });

/** Cover-fit the source image into the target rect (returns {sx,sy,sw,sh}). */
const coverFit = (img, targetW, targetH) => {
  const targetAR = targetW / targetH;
  const srcAR = img.width / img.height;
  let sw = img.width, sh = img.height, sx = 0, sy = 0;
  if (srcAR > targetAR) {
    sw = img.height * targetAR;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / targetAR;
    sy = (img.height - sh) / 2;
  }
  return { sx, sy, sw, sh };
};

const drawCaption = (ctx, W, H, top, bottom) => {
  const fontSize = Math.round(W * 0.085);
  const drawLine = (txt, y) => {
    if (!txt) return;
    ctx.textAlign = "center";
    ctx.lineJoin = "round";
    ctx.font = `800 ${fontSize}px Impact, "Arial Black", sans-serif`;
    const upper = String(txt).toUpperCase();
    ctx.lineWidth = Math.max(8, Math.round(fontSize * 0.16));
    ctx.strokeStyle = "#000";
    ctx.strokeText(upper, W / 2, y, W - W * 0.08);
    ctx.fillStyle = "#fff";
    ctx.fillText(upper, W / 2, y, W - W * 0.08);
  };
  drawLine(top, fontSize * 1.4);
  drawLine(bottom, H - fontSize * 0.6);

  // ----- Watermark: $PAUL · @alienpaul_sol · <contract> -----
  // Centered along the bottom of the canvas.
  ctx.textAlign = "center";
  const wmFontSize = Math.round(W * 0.018);
  ctx.font = `600 ${wmFontSize}px "IBM Plex Mono", monospace`;
  ctx.lineWidth = Math.round(W * 0.004);
  ctx.strokeStyle = "rgba(0,0,0,0.75)";
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  const wmY = H - Math.round(W * 0.014);
  const wmText = `$PAUL · @alienpaul_sol · ${CONTRACT_ADDRESS}`;
  ctx.strokeText(wmText, W / 2, wmY);
  ctx.fillText(wmText, W / 2, wmY);
};

const renderMeme = async (template, bg, top, bottom) => {
  const canvas = document.createElement("canvas");
  canvas.width = OUT_W; canvas.height = OUT_H;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // ----- 1. Background -----
  if (bg.type === "default") {
    try {
      const baseImg = await loadImage(template.src);
      const { sx, sy, sw, sh } = coverFit(baseImg, OUT_W, OUT_H);
      ctx.drawImage(baseImg, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
    } catch (e) {
      console.warn("[meme] base image failed", e);
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, OUT_W, OUT_H);
    }
    drawCaption(ctx, OUT_W, OUT_H, top, bottom);
    return canvas.toDataURL("image/png");
  }

  if (bg.type === "scene") {
    try {
      const sceneImg = await loadImage(bg.src);
      const { sx, sy, sw, sh } = coverFit(sceneImg, OUT_W, OUT_H);
      ctx.drawImage(sceneImg, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
    } catch (e) {
      console.warn("[meme] scene image failed, using black", bg.src, e);
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, OUT_W, OUT_H);
    }
  } else {
    // colour
    ctx.fillStyle = bg.value;
    ctx.fillRect(0, 0, OUT_W, OUT_H);
  }

  // ----- 2. Paul cutout — must succeed; if it fails we still return what we have -----
  try {
    const cutout = await loadImage(template.cutout);
    const fit = template.cutoutFit;
    if (fit && fit.scale && fit.scale !== 1) {
      const baseAR = cutout.width / cutout.height;
      const canvasAR = OUT_W / OUT_H;
      let drawW, drawH;
      if (baseAR > canvasAR) { drawH = OUT_H; drawW = OUT_H * baseAR; }
      else                   { drawW = OUT_W; drawH = OUT_W / baseAR; }
      drawW *= fit.scale;
      drawH *= fit.scale;
      const dx = (OUT_W - drawW) / 2;
      const anchorY = typeof fit.anchorY === "number" ? fit.anchorY : 0.5;
      const dy = (OUT_H - drawH) * anchorY;
      ctx.drawImage(cutout, dx, dy, drawW, drawH);
    } else {
      const { sx, sy, sw, sh } = coverFit(cutout, OUT_W, OUT_H);
      ctx.drawImage(cutout, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
    }
  } catch (e) {
    console.warn("[meme] cutout failed", template.cutout, e);
    // still return the bg + caption rather than throwing; user will at least see the bg change.
  }

  // ----- 3. Caption + watermark -----
  drawCaption(ctx, OUT_W, OUT_H, top, bottom);
  return canvas.toDataURL("image/png");
};

// ----- UI bits -----
const TemplateChip = ({ template, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`p-0 border-2 transition-colors overflow-hidden bg-ink ${selected ? 'border-white' : 'border-white-2 hover:border-white'}`}
    style={{ borderRadius: 4, boxShadow: selected ? '4px 4px 0 0 rgba(0,0,0,0.6)' : '3px 3px 0 0 rgba(0,0,0,0.4)' }}
    data-testid={`template-${template.id}`}
    title={template.label}
  >
    <img
      src={template.src}
      alt={template.label}
      className="block w-full h-20 object-cover"
      style={{ objectPosition: template.objectPosition }}
    />
    <div className="font-mono text-[10px] py-1.5 tracking-[0.15em] truncate text-white bg-ink text-center">
      {template.label}
    </div>
  </button>
);

const BgChip = ({ bg, selected, onClick }) => {
  const isScene = bg.type === "scene";
  const isColor = bg.type === "color";
  const isDefault = bg.type === "default";

  return (
    <button
      onClick={onClick}
      title={bg.label}
      aria-label={bg.label}
      className={`relative overflow-hidden border-2 transition-transform ${selected ? 'border-white scale-105' : 'border-white-2 hover:border-white'}`}
      style={{
        borderRadius: 4,
        boxShadow: selected ? '0 0 0 2px rgba(0,0,0,0.6), 0 0 14px rgba(255,255,255,0.35)' : '0 0 0 1px rgba(0,0,0,0.4)',
      }}
      data-testid={`bg-${bg.id}`}
    >
      {isScene && (
        <img src={bg.src} alt={bg.label} className="block w-full h-16 object-cover" />
      )}
      {isColor && (
        <div className="w-full h-16" style={{ background: bg.value }} />
      )}
      {isDefault && (
        <div
          className="w-full h-16 relative"
          style={{ background: 'radial-gradient(circle at 30% 30%, #1a1a2a 0%, #000 70%)' }}
        >
          <span className="absolute top-2 left-3 w-1 h-1 rounded-full bg-white" />
          <span className="absolute top-5 left-7 w-0.5 h-0.5 rounded-full bg-white" />
          <span className="absolute bottom-3 right-3 w-0.5 h-0.5 rounded-full bg-white" />
        </div>
      )}
      <div className="font-mono text-[10px] py-1.5 px-1 tracking-[0.12em] truncate text-white bg-ink text-center">
        {bg.label}
      </div>
    </button>
  );
};

// ============================================================================

export default function MemePage() {
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [bg, setBg] = useState(BACKGROUNDS[0]);
  const [topic, setTopic] = useState("");
  const [captions, setCaptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const renderToken = useRef(0);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/meme/captions`, {
        topic,
        template_id: template.id,
      });
      setCaptions(data.captions || []);
      setSelected(0);
      toast.success("Captions beamed in");
    } catch (e) {
      console.error(e);
      toast.error("Signal lost. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const current = captions[selected];

  useEffect(() => {
    const myToken = ++renderToken.current;
    (async () => {
      try {
        const top    = current ? current.top    : "";
        const bottom = current ? current.bottom : "";
        const png = await renderMeme(template, bg, top, bottom);
        // ignore stale renders (race protection)
        if (myToken === renderToken.current) setPreview(png);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [current, template, bg]);

  const download = async () => {
    if (!preview) return;
    setDownloading(true);
    try {
      const a = document.createElement("a");
      a.href = preview;
      a.download = `paul-meme-${template.id}-${bg.id}-${Date.now()}.png`;
      a.click();
      toast.success("Meme downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    }
    setTimeout(() => setDownloading(false), 600);
  };

  const colorBgs   = BACKGROUNDS.filter(b => b.type === "color");
  const sceneBgs   = BACKGROUNDS.filter(b => b.type === "scene");
  const defaultBg  = BACKGROUNDS.find(b => b.type === "default");

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="meme-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-mono text-xs text-green tracking-[0.3em]">MODULE / M-04</span>
        <span className="font-mono text-xs text-white/70">claude-sonnet-4.5 · 4K · 4:5</span>
      </div>
      <h1 className="font-neon text-5xl md:text-7xl text-white glow-white mt-3">
        <span className="font-mono font-extrabold mr-2">$</span>MEME LAB
      </h1>
      <p className="font-type text-white text-lg mt-2 max-w-2xl">
        Pick a Paul. Pick a backdrop. Drop a topic. I'll write three captions, stamp one on a
        crisp 4K image of my beautiful face at the perfect 4:5 ratio for socials.
      </p>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        {/* Left: controls */}
        <div className="lg:col-span-5 space-y-8">
          {/* Templates */}
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] text-green mb-3">01 · PICK A PAUL</div>
            <div className="grid grid-cols-5 gap-3" data-testid="template-grid">
              {TEMPLATES.map(t => (
                <TemplateChip key={t.id} template={t} selected={template.id === t.id} onClick={() => setTemplate(t)} />
              ))}
            </div>
          </div>

          {/* Backgrounds */}
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] text-green mb-3">02 · CHOOSE A BACKDROP</div>

            {/* Default + colours */}
            <div className="font-mono text-[10px] tracking-[0.25em] text-white/50 mb-2 uppercase">Default & colours</div>
            <div className="grid grid-cols-4 gap-2.5 mb-4" data-testid="bg-row-colors">
              <BgChip bg={defaultBg} selected={bg.id === defaultBg.id} onClick={() => setBg(defaultBg)} />
              {colorBgs.map(b => (
                <BgChip key={b.id} bg={b} selected={bg.id === b.id} onClick={() => setBg(b)} />
              ))}
            </div>

            {/* Scenes */}
            <div className="font-mono text-[10px] tracking-[0.25em] text-white/50 mb-2 uppercase">Scenes</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="bg-row-scenes">
              {sceneBgs.map(b => (
                <BgChip key={b.id} bg={b} selected={bg.id === b.id} onClick={() => setBg(b)} />
              ))}
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-white/55 mt-3 uppercase">
              selected: {bg.label}
            </div>
          </div>

          {/* Topic */}
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] text-green mb-3">03 · WHAT'S THE JOKE ABOUT?</div>
            <input
              className="p-input"
              placeholder="e.g. Congress, bad Wi-Fi, Roswell, Reese's Pieces..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && generate()}
              data-testid="topic-input"
            />
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              onClick={generate}
              disabled={loading}
              className="btn-paul"
              data-testid="generate-btn"
            >
              {loading ? <><Loader2 size={14} className="animate-spin" /> BEAMING</> : <><Sparkles size={14} /> GENERATE CAPTIONS</>}
            </button>
            {captions.length > 0 && (
              <button onClick={generate} className="btn-paul green" data-testid="regenerate-btn">
                <RefreshCcw size={14} /> AGAIN
              </button>
            )}
          </div>

          {captions.length > 0 && (
            <div className="space-y-3" data-testid="captions-list">
              <div className="font-mono text-[11px] tracking-[0.25em] text-green mb-1">04 · PICK ONE</div>
              {captions.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(i)}
                  className={`w-full text-left p-4 border-2 transition-colors bg-paper ${selected === i ? 'border-purple' : 'border-ink hover:border-purple'}`}
                  style={{ borderRadius: 4, boxShadow: selected === i ? '4px 4px 0 0 #7A3EFF' : '3px 3px 0 0 rgba(0,0,0,0.5)' }}
                  data-testid={`caption-option-${i}`}
                >
                  <div className="font-type text-ink text-lg leading-snug">{c.top}</div>
                  <div className="font-type text-purple text-lg leading-snug">{c.bottom}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: preview (4:5) */}
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] tracking-[0.25em] text-green mb-3">05 · PREVIEW · 2160 × 2700 · 4:5</div>
          <div
            className="relative border-2 border-white overflow-hidden bg-ink mx-auto"
            style={{ borderRadius: 4, aspectRatio: '4 / 5', maxWidth: 540, boxShadow: '6px 6px 0 0 rgba(0,0,0,0.6)' }}
            data-testid="preview-area"
          >
            {preview && (
              <img src={preview} alt="Paul meme preview" className="w-full h-full object-cover" data-testid="preview-image" />
            )}
            {!current && preview && (
              <div className="absolute top-3 left-3 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase bg-black/70 text-white border border-white/40 rounded-sm">
                awaiting captions<span className="dotdotdot"><span>.</span><span>.</span><span>.</span></span>
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-center lg:justify-end">
            <button
              onClick={download}
              disabled={!preview || downloading}
              className="btn-paul green"
              data-testid="download-btn"
            >
              <Download size={14} /> {downloading ? "DOWNLOADED" : "DOWNLOAD PNG · 4K"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
